<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  isConversationRouteName,
  isConversationsAreaRouteName,
} from 'dashboard/helper/conversationRoutes';

// SiteUp (2026-08-26, mockup A aprovado pelo Lucas): a barra inferior do PWA
// virou uma pílula de vidro flutuante, no material do iOS 26 — desfoque
// profundo, brilho especular na borda e uma "lente" que desliza até o destino
// ativo com física de mola, esticando no caminho. Ao rolar a lista para
// baixo, a pílula se recolhe para o destino ativo (como a tab bar da Apple);
// rolar para cima, ou tocá-la, expande de volta.
//
// Fase 2 registrada: hoje o layout reserva max-md:pb-14 sob a barra, então o
// conteúdo em repouso termina rente à pílula em vez de correr por baixo dela.
// O efeito pleno (edge-to-edge) exige dar padding interno aos scrollers de
// cada destino móvel — fatia própria.

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isKanbanEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, 'kanban_board')
);

const isConversationRoute = computed(() => isConversationRouteName(route.name));

const matches = (name, patterns) =>
  patterns.some(p => (name || '').startsWith(p) || (name || '').includes(p));

const navItems = computed(() => {
  const items = [
    {
      id: 'hub',
      label: t('HUB.TITLE'),
      icon: 'i-ri-apps-2-line',
      activeIcon: 'i-ri-apps-2-fill',
      to: accountScopedRoute('focus_hub'),
      // A tela de uma área do Hub (hub/:worldId) também é o Hub.
      match: name => name === 'focus_hub' || name === 'hub_world_home',
    },
    {
      id: 'conversations',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-ri-chat-1-line',
      activeIcon: 'i-ri-chat-1-fill',
      to: accountScopedRoute('home'),
      match: isConversationsAreaRouteName,
    },
    {
      id: 'contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-ri-contacts-line',
      activeIcon: 'i-ri-contacts-fill',
      to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
      match: name => matches(name, ['contacts', 'companies']),
    },
  ];

  if (isKanbanEnabled.value) {
    items.push({
      id: 'kanban',
      label:
        store.getters['kanban/getKanbanTitle'] || t('HUB.DESTINATIONS.KANBAN'),
      icon: 'i-ri-layout-grid-line',
      activeIcon: 'i-ri-layout-grid-fill',
      to: accountScopedRoute('kanban_board'),
      match: name => matches(name, ['kanban']),
    });
  }

  return items;
});

const isActive = item => item.match(route.name);

// A faixa inferior respeita o gesto do iPhone via safe area.
const safeAreaStyle = {
  bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
};

// ---------------------------------------------------------------------------
// Lente de vidro: mola de verdade, não cubic-bezier.
// ---------------------------------------------------------------------------
const LENS_WIDTH = 76;
const navRef = ref(null);
const lensRef = ref(null);
const sweeping = ref(false);
const minimized = ref(false);

const reducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let cancelSpring = null;

// Integração explícita de massa-mola (padrão dos springs da Apple). O squash
// vem da velocidade: a lente estica no meio do caminho e assenta redonda.
const spring = (from, to, onFrame) => {
  let x = from;
  let v = 0;
  let raf = null;
  const dt = 1 / 60;
  const step = () => {
    v += (-340 * (x - to) - 26 * v) * dt;
    x += v * dt;
    onFrame(x, v);
    if (Math.abs(x - to) < 0.15 && Math.abs(v) < 0.15) {
      onFrame(to, 0);
      return;
    }
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => raf && cancelAnimationFrame(raf);
};

const lensTargetLeft = () => {
  const nav = navRef.value;
  const active = nav?.querySelector('.glass-item.on');
  if (!nav || !active) return null;
  const navBox = nav.getBoundingClientRect();
  const itemBox = active.getBoundingClientRect();
  return itemBox.left - navBox.left + (itemBox.width - LENS_WIDTH) / 2;
};

const moveLens = instant => {
  const lens = lensRef.value;
  if (!lens) return;
  const left = lensTargetLeft();
  // Tela sem item na barra (Tarefas, Automações...): a lente some em vez de
  // ficar parada sobre o destino anterior. Ao voltar, reaparece já no lugar.
  const wasHidden = lens.style.opacity === '0';
  lens.style.opacity = left === null ? '0' : '';
  if (left === null) return;
  cancelSpring?.();
  if (instant || wasHidden || reducedMotion) {
    lens.style.transform = `translateX(${left}px)`;
    return;
  }
  const current = new DOMMatrix(getComputedStyle(lens).transform).m41 || 0;
  sweeping.value = false;
  requestAnimationFrame(() => {
    sweeping.value = true;
  });
  cancelSpring = spring(current, left, (x, v) => {
    const squash = Math.min(Math.abs(v) * 0.0011, 0.38);
    lens.style.transform = `translateX(${x}px) scaleX(${1 + squash}) scaleY(${1 - squash * 0.55})`;
  });
};

watch(
  () => route.name,
  () => nextTick(() => moveLens(false))
);
// O item do Kanban entra depois (feature flag resolve tarde): reposiciona.
watch(
  () => navItems.value.length,
  () => nextTick(() => moveLens(true))
);

// ---------------------------------------------------------------------------
// Recolher ao rolar (iOS 26): qualquer scroller do app conta. Ouvimos em
// captura porque cada destino móvel rola num container diferente.
// ---------------------------------------------------------------------------
const lastTops = new WeakMap();
let expandTimer = null;

const expand = () => {
  if (!minimized.value) return;
  minimized.value = false;
  clearTimeout(expandTimer);
  // A pílula reabre com transição CSS; a lente se recoloca depois dela.
  expandTimer = setTimeout(() => moveLens(true), 580);
};

const onAnyScroll = event => {
  const el = event.target;
  if (!(el instanceof Element) || isConversationRoute.value) return;
  if (el.scrollHeight - el.clientHeight < 120) return;
  const top = el.scrollTop;
  const last = lastTops.get(el) ?? top;
  lastTops.set(el, top);
  if (reducedMotion) return;
  if (top > last + 4 && top > 80 && !minimized.value) minimized.value = true;
  else if ((top < last - 4 || top < 48) && minimized.value) expand();
};

let resizeObserver = null;

onMounted(() => {
  nextTick(() => moveLens(true));
  window.addEventListener('scroll', onAnyScroll, {
    capture: true,
    passive: true,
  });
  if (typeof ResizeObserver !== 'undefined' && navRef.value) {
    resizeObserver = new ResizeObserver(() => {
      if (!minimized.value) moveLens(true);
    });
    resizeObserver.observe(navRef.value);
  }
});

onBeforeUnmount(() => {
  cancelSpring?.();
  clearTimeout(expandTimer);
  window.removeEventListener('scroll', onAnyScroll, { capture: true });
  resizeObserver?.disconnect();
});
</script>

<template>
  <nav
    v-if="!isConversationRoute"
    id="mobile-bottom-nav"
    ref="navRef"
    :aria-label="t('SIDEBAR.MOBILE_NAV')"
    class="glass-nav md:hidden"
    :class="{ mini: minimized, sweep: sweeping }"
    :style="safeAreaStyle"
    @click="expand"
  >
    <span ref="lensRef" class="glass-lens" aria-hidden="true">
      <span class="glass-sheen" />
    </span>
    <router-link
      v-for="item in navItems"
      :key="item.id"
      :to="item.to"
      :data-testid="item.id === 'hub' ? 'mobile-nav-hub' : undefined"
      class="glass-item"
      :class="{ on: isActive(item) }"
      :aria-current="isActive(item) ? 'page' : undefined"
    >
      <span class="glass-ic">
        <span class="i-line">
          <Icon :icon="item.icon" class="size-[22px]" />
        </span>
        <span class="i-fill">
          <Icon :icon="item.activeIcon" class="size-[22px]" />
        </span>
      </span>
      <span class="glass-lb">{{ item.label }}</span>
    </router-link>
  </nav>
</template>

<style scoped>
/* Material Liquid Glass.
 *
 * As cores saem dos TOKENS do design system (--solid-*, --slate-*, --alpha-*),
 * que já invertem sozinhos dentro de .dark. A primeira versão pintava o claro
 * com rgba fixo e trocava por um seletor :global(.dark) — no aparelho a barra
 * saiu branca sobre o tema escuro, porque a troca não pegou. Token não erra
 * tema; seletor erra. */
/* O `md:hidden` da classe nao basta: o Tailwind gera `.md\:hidden` (0-1-0) e o
 * scoped CSS gera `.glass-nav[data-v-hash]` (0-2-0), entao o `display: flex`
 * daqui vencia a regra que escondia a barra e ela aparecia no desktop.
 * Esconder por padrao e mostrar so abaixo do breakpoint resolve dentro do
 * proprio componente, sem depender de quem ganha a disputa. */
.glass-nav {
  display: none;
  position: fixed;
  left: 16px;
  right: 16px;
  z-index: 40;
  align-items: stretch;
  height: 62px;
  padding: 0 6px;
  @apply rounded-[var(--workspace-radius,31px)];
  background: linear-gradient(
    180deg,
    rgb(var(--solid-2) / 0.72),
    rgb(var(--solid-1) / 0.82)
  );
  backdrop-filter: blur(26px) saturate(1.9);
  -webkit-backdrop-filter: blur(26px) saturate(1.9);
  border: 1px solid rgba(var(--alpha-2));
  box-shadow:
    inset 0 1px 0 rgba(var(--alpha-2)),
    0 18px 45px rgba(var(--black-alpha-2));
  transition:
    width 0.55s cubic-bezier(0.32, 1.3, 0.45, 1),
    left 0.55s cubic-bezier(0.32, 1.3, 0.45, 1),
    margin 0.55s cubic-bezier(0.32, 1.3, 0.45, 1),
    transform 0.5s cubic-bezier(0.32, 1.35, 0.45, 1);
}
@media (max-width: 767px) {
  .glass-nav {
    display: flex;
  }
}
.glass-nav.mini {
  left: 50%;
  right: auto;
  width: 92px;
  margin-left: -46px;
  transform: translateY(2px);
}

.glass-item {
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  max-width: 120px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: rgb(var(--slate-11));
  text-decoration: none;
  transition:
    max-width 0.5s cubic-bezier(0.32, 1.3, 0.45, 1),
    opacity 0.3s,
    transform 0.4s,
    color 0.2s;
}
.glass-nav.mini .glass-item:not(.on) {
  max-width: 0;
  opacity: 0;
  transform: scale(0.6);
  overflow: hidden;
  pointer-events: none;
}
.glass-item.on {
  color: rgb(var(--slate-12));
}

.glass-ic {
  position: relative;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
}
.i-line,
.i-fill {
  grid-area: 1 / 1;
  display: grid;
  place-items: center;
  transition:
    opacity 0.18s,
    transform 0.34s cubic-bezier(0.34, 1.7, 0.5, 1);
}
.i-fill {
  opacity: 0;
  transform: scale(0.6);
}
.glass-item.on .i-fill {
  opacity: 1;
  transform: scale(1);
}
.glass-item.on .i-line {
  opacity: 0;
}
.glass-item.on .glass-ic {
  animation: glass-pop 0.42s cubic-bezier(0.34, 1.9, 0.5, 1);
}
@keyframes glass-pop {
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.28) translateY(-2px);
  }
  100% {
    transform: scale(1);
  }
}

.glass-lb {
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
}

/* A lente é luz sobre o vidro: no escuro clareia, no claro escurece. Como não
 * há token que troque de direção, a mistura resolve — color-mix entre o branco
 * e o próprio fundo da superfície, que já é do tema. */
.glass-lens {
  position: absolute;
  top: 5px;
  bottom: 5px;
  left: 0;
  width: 76px;
  @apply rounded-[var(--workspace-radius,26px)];
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgb(var(--slate-12) / 0.14),
    rgb(var(--slate-12) / 0.05)
  );
  border: 1px solid rgb(var(--slate-12) / 0.16);
  box-shadow:
    inset 0 1px 1px rgb(var(--slate-12) / 0.22),
    0 6px 18px rgba(var(--black-alpha-2));
  overflow: hidden;
  will-change: transform;
}
.glass-sheen {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -60%;
  width: 55%;
  background: linear-gradient(
    100deg,
    transparent,
    rgb(var(--slate-12) / 0.3),
    transparent
  );
  opacity: 0;
  transform: skewX(-18deg);
}
.glass-nav.sweep .glass-sheen {
  animation: glass-sweep 0.55s ease-out;
}
@keyframes glass-sweep {
  0% {
    left: -60%;
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
  100% {
    left: 110%;
    opacity: 0;
  }
}

/* Sem suporte a backdrop-filter o vidro vira vidro fosco opaco, nunca uma
 * faixa transparente que deixa o texto passar por baixo. */
@supports not (
  (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))
) {
  .glass-nav {
    background: rgb(var(--solid-1));
  }
}

@media (prefers-reduced-motion: reduce) {
  .glass-nav,
  .glass-item,
  .i-line,
  .i-fill {
    transition: none !important;
    animation: none !important;
  }
  .glass-nav.sweep .glass-sheen,
  .glass-item.on .glass-ic {
    animation: none !important;
  }
}
</style>
